import { FC, ReactElement, ReactNode, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import ActivityFrame from "../../../components/activity-frame/activity-frame";
import Breadcrumb from "../../../components/breadcrumb/breadcrumb";
import { useDialog } from "../../../components/dialog/DialogContext";
import ReportModal from "../../../components/report/ReportModal";
import Tabs, { getTabsListFromLabels } from "../../../components/tabs/tabs";
import { getCountsCategory, REPORT_TYPE_CATEGORY, TYPE_PER_REPORT_TYPE } from "../../../constants/system-constants";
import { useLocale } from "../../../contexts/localeContext";
import Restricted from "../../../contexts/PermissionProvider/RestrictedWrapper";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import plusIcon from "../../../images/Plus.svg";
import { Category } from "../../../models/Category";
import { Report as ReportModel } from "../../../models/Report";
import SystemStat from "../../../models/SystemStat";
import { storeReport } from "../../../services/reportService";
import { getSystemStats } from "../../../services/systemService";
import { getErrorMessage, showError } from "../../../services/utilities/ServiceError";
import { IAppState } from "../../../store/types";
import AllReport from "./AllReport";

const Reports: FC = () => {
    const [selected, setSelected] = useState(0);
    const [busy, setBusy] = useState(false);
    const history = useHistory();
    const [categories, setCategories] = useState<Category[]>([]);
    const [tabContent, setTabContent] = useState<ReactNode[]>([]);
    const [showModal, setShowModal] = useState(false);
    const { translate } = useLocale();
    const user = useSelector((state: IAppState) => (state.userInfo));
    const [currentItem, setCurrentItem] = useState<ReportModel>({
        id: 0,
        name: '',
        typeId: "",
        reportSubTypeId: ""
    })
    const dialog = useDialog();

    useEffect(() => {
        buildCategoriesData();
    }, []);

    const buildCategoriesData = async () => {
        try {
            setBusy(true);
            const showTabCounts = window.sessionStorage.getItem("showTabCounts") === "1";
            const systemStat: SystemStat | undefined = showTabCounts ? await getSystemStats(TYPE_PER_REPORT_TYPE) : undefined;
            const count = systemStat ? getCountsCategory(systemStat, REPORT_TYPE_CATEGORY.ALL_RPTS) : 0;
            setCategories([
                {
                    displayName: translate.byKey(TranslationKeys.all),
                    heading: translate.byKey(TranslationKeys.all),
                    value: "",
                    id: 0,
                    count: count
                },
            ]);

            setBusy(false);
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    useEffect(() => {
        if (categories.length > 0) {
            const newTabContent: ReactElement[] = [
                <AllReport
                    setCurrentItem={setCurrentItem}
                    setBusy={setBusy}
                    categories={categories}
                    setCategories={setCategories}
                    openModal={setShowModal}
                    isModalOpen={showModal}
                    userProfileId={user.profileId} />
            ];
            setTabContent(newTabContent);
        }
    }, [categories]);

    const onModalClose = () => {
        setCurrentItem({
            id: 0,
            name: '',
            typeId: "",
            reportSubTypeId: ""
        })
        setShowModal(false)
    }

    const saveItem = (values) => {
        const report: ReportModel = {
            id: values.id ?? 0,
            name: values.name,
            description: values.description,
            typeId: values.typeId,
            reportSubTypeId: values.reportSubTypeId,
            userLoginId: values.id ? values.userLoginId : user.id
        }

        const isNew = values.id ? false : true;
        setBusy(true);
        storeReport(report, user.id, isNew)
            .then(async (value) => {
                toast.success(translate.byKey(TranslationKeys.reportSaved));
                if (currentItem.id === 0) {
                    setCategories([{ ...categories[0], count: (categories[0]?.count ?? 1) + 1 }]);
                }
                setShowModal(false);
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
            })
            .finally(() => {
                setBusy(false);
            });
    }

    return <ActivityFrame
        busy={busy}
        className="flex flex-grow overflow-y-hidden">
        <div className="pt-4 pl-4 pb-4 flex flex-col flex-grow ">
            <Breadcrumb
                path={[
                    {
                        value: "/access-control/reports",
                        displayName: translate.byKey(TranslationKeys.reports),
                    },
                ]}
                onClick={(item) => history.push(item.value)} />
            <Tabs
                className="overflow-y-hidden"
                tabs={getTabsListFromLabels(categories)}
                onSelect={(i) => setSelected(i)}
                setSelectedTab={setSelected}
                selected={selected}
                onAdd={() => { }}
                add={false}
                tabStyle={{}}>
                {tabContent[selected]}
                <div className="flex flex-col flex-grow overflow-y-hidden mb-viewport">
                    <div className="flex flex-row justify-end button-cluster">
                        <Restricted to={`add.reports.element`}>
                            <button onClick={() => setShowModal(true)} className="flex rounded-lg bg-lime text-white p-2 shadow-lg" id='reportsAddButton'>
                                <img
                                    src={plusIcon}
                                    className="px-2 w-8 py-1"
                                    alt="" />
                                <span className="mr-2">{translate.byKey(TranslationKeys.add)}</span>
                            </button>
                        </Restricted>
                    </div>
                </div>
            </Tabs>
        </div>
        <ReportModal currentItem={currentItem} isOpen={showModal} onClose={onModalClose} saveReport={saveItem} />
    </ActivityFrame>
}

export default Reports;
